import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { versionApi, VersionMetadata } from '../services/api';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  folder: string;
  filename: string;
  onVersionRestored?: () => void;
}

export const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  folder,
  filename,
  onVersionRestored,
}) => {
  const [versions, setVersions] = useState<VersionMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringVersion, setRestoringVersion] = useState<string | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadVersionHistory();
    }
  }, [open, folder, filename]);

  const loadVersionHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const versionList = await versionApi.getVersionHistory(folder, filename);
      setVersions(versionList);
    } catch (err: any) {
      console.error('Failed to load version history:', err);
      // Check if it's a 404 - file has no version history (legacy file)
      if (err.response?.status === 404 || err.message?.includes('404')) {
        setError('This file does not have version history. Legacy files need to be migrated to enable versioning.');
      } else {
        setError(err.message || 'Failed to load version history');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVersion = async (version: string) => {
    try {
      const blob = await versionApi.downloadVersion(folder, filename, version);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${version}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download version:', err);
      setError(err.message || 'Failed to download version');
    }
  };

  const handleRestoreVersion = async (version: string) => {
    if (!confirm(`Are you sure you want to restore ${filename} to version ${version}? This will create a new version based on ${version}.`)) {
      return;
    }

    setRestoringVersion(version);
    setError(null);
    try {
      await versionApi.restoreVersion(folder, filename, version);
      await loadVersionHistory();
      if (onVersionRestored) {
        onVersionRestored();
      }
    } catch (err: any) {
      console.error('Failed to restore version:', err);
      setError(err.message || 'Failed to restore version');
    } finally {
      setRestoringVersion(null);
    }
  };

  const handleDeleteVersion = async (version: string) => {
    if (!confirm(`Are you sure you want to delete version ${version}? This action cannot be undone.`)) {
      return;
    }

    setDeletingVersion(version);
    setError(null);
    try {
      await versionApi.deleteVersion(folder, filename, version);
      await loadVersionHistory();
    } catch (err: any) {
      console.error('Failed to delete version:', err);
      setError(err.message || 'Failed to delete version');
    } finally {
      setDeletingVersion(null);
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">Version History: {filename}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !versions || versions.length === 0 ? (
          <Alert severity="info">No version history available for this file.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Version</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Size</strong></TableCell>
                  <TableCell><strong>Uploaded By</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Tags</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((version) => (
                  <TableRow
                    key={version.version}
                    sx={{
                      backgroundColor: version.isCurrent ? 'action.selected' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {version.version}
                        {version.isCurrent && (
                          <Chip label="Current" color="primary" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(version.timestamp)}</TableCell>
                    <TableCell>{formatSize(version.size)}</TableCell>
                    <TableCell>{version.uploadedBy || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {version.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {version.tags && version.tags.length > 0 ? (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {version.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="Download this version">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadVersion(version.version)}
                            disabled={!!restoringVersion || !!deletingVersion}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!version.isCurrent && (
                          <>
                            <Tooltip title="Restore this version">
                              <IconButton
                                size="small"
                                onClick={() => handleRestoreVersion(version.version)}
                                disabled={restoringVersion === version.version || !!deletingVersion}
                              >
                                {restoringVersion === version.version ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <RestoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete this version">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteVersion(version.version)}
                                disabled={deletingVersion === version.version || !!restoringVersion}
                                color="error"
                              >
                                {deletingVersion === version.version ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {versions && versions.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Total versions: {versions.length} | Current version: {versions.find(v => v.isCurrent)?.version}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
